import React, { useEffect, useRef, useCallback } from 'react';
import { getWaveformColor } from '../utils/colorInterpolation';

interface WaveformVisualizerProps {
  stream: MediaStream;
  className?: string;
  remainingSeconds?: number;
  maxDuration?: number;
}

interface WaveBar {
  height: number;
  position: number;
  opacity: number;
}

// Waveform visualization configuration
const WAVEFORM_CONFIG = {
  // Visual appearance
  BAR_WIDTH: 3, // Width of each bar in pixels
  BAR_MIN_HEIGHT: 2, // Minimum bar height in pixels
  BAR_MAX_HEIGHT: 100, // Maximum bar height in pixels (restored original)
  BAR_GAP: 1, // Gap between bars in pixels
  BAR_RADIUS: 1, // Border radius for rounded bars

  // Animation
  SCROLL_SPEED: 0.5, // Pixels per frame to move left (adjusted for smooth canvas animation)
  UPDATE_INTERVAL: 50, // Milliseconds between adding new bars

  // Audio processing
  AUDIO_MIDPOINT: 128, // Midpoint for audio data (8-bit)
  AUDIO_SCALE: 128, // Scale factor for audio normalization
  AMPLITUDE_MULTIPLIER: 150, // Multiplier for visual amplitude (restored original)

  // Canvas
  DEVICE_PIXEL_RATIO: window.devicePixelRatio || 1, // For high DPI displays

  // Defaults
  DEFAULT_MAX_DURATION: 60, // Default max duration in seconds
} as const;

/**
 * WaveformVisualizer uses Canvas API for smooth 60fps animation without React re-renders.
 * Audio data flows from right to left as vertical bars with color transitions based on time.
 */
const WaveformVisualizer = ({
  stream,
  className,
  remainingSeconds,
  maxDuration = WAVEFORM_CONFIG.DEFAULT_MAX_DURATION,
}: WaveformVisualizerProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const barsRef = useRef<WaveBar[]>([]);
  const lastUpdateTimeRef = useRef(Date.now());
  const currentColorRef = useRef<string>('');

  /**
   * Resize canvas to match container dimensions with device pixel ratio
   */
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const rect = container.getBoundingClientRect();
    const dpr = WAVEFORM_CONFIG.DEVICE_PIXEL_RATIO;

    // Set display size
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;

    // Set actual size in memory (scaled for DPI)
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    // Scale context to match device pixel ratio
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(dpr, dpr);
    }
  }, []);

  /**
   * Draw bars on canvas
   */
  const drawBars = useCallback(
    (ctx: CanvasRenderingContext2D, canvasWidth: number, canvasHeight: number) => {
      // Clear canvas
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

      // Get current color based on remaining time
      const color = getWaveformColor(remainingSeconds, maxDuration);
      currentColorRef.current = color;

      // Draw each bar
      barsRef.current.forEach((bar) => {
        ctx.fillStyle = color;
        ctx.globalAlpha = bar.opacity;

        const barHeightPixels = (bar.height / 100) * canvasHeight;
        const y = (canvasHeight - barHeightPixels) / 2;

        // Draw rounded rectangle for each bar
        ctx.beginPath();
        ctx.roundRect(
          bar.position,
          y,
          WAVEFORM_CONFIG.BAR_WIDTH,
          barHeightPixels,
          WAVEFORM_CONFIG.BAR_RADIUS
        );
        ctx.fill();
      });

      // Reset alpha
      ctx.globalAlpha = 1;
    },
    [remainingSeconds, maxDuration]
  );

  /**
   * Calculate amplitude from audio data
   */
  const calculateAmplitude = useCallback((dataArray: Uint8Array): number => {
    let sum = 0;
    const length = dataArray.length;

    for (let i = 0; i < length; i++) {
      const value = dataArray[i];
      if (value !== undefined) {
        const amplitude = Math.abs(value - WAVEFORM_CONFIG.AUDIO_MIDPOINT);
        sum += amplitude;
      }
    }

    return length > 0 ? sum / length / WAVEFORM_CONFIG.AUDIO_SCALE : 0;
  }, []);

  useEffect(() => {
    let isCancelled = false;

    // Initial canvas setup
    resizeCanvas();

    // Handle window resize
    const handleResize = () => resizeCanvas();
    window.addEventListener('resize', handleResize);

    const setup = async () => {
      try {
        // Check if AudioContext is supported
        if (!window.AudioContext) {
          console.warn('WaveformVisualizer: AudioContext not supported');
          return;
        }

        const audioContext = new window.AudioContext();
        audioContextRef.current = audioContext;

        // Resume context if suspended (for browsers requiring user gesture)
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        // Set up audio analysis
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.3;
        analyserRef.current = analyser;

        source.connect(analyser);

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        dataArrayRef.current = dataArray;

        // Animation loop
        const animate = () => {
          if (isCancelled) return;

          const canvas = canvasRef.current;
          const ctx = canvas?.getContext('2d');
          if (!canvas || !ctx || !analyser || !dataArray) return;

          const now = Date.now();
          const canvasWidth = canvas.width / WAVEFORM_CONFIG.DEVICE_PIXEL_RATIO;
          const canvasHeight = canvas.height / WAVEFORM_CONFIG.DEVICE_PIXEL_RATIO;

          // Get audio data
          analyser.getByteTimeDomainData(dataArray);
          const amplitude = calculateAmplitude(dataArray);

          // Update bar positions (move left)
          barsRef.current = barsRef.current
            .map((bar) => ({
              ...bar,
              position: bar.position - WAVEFORM_CONFIG.SCROLL_SPEED,
              opacity: Math.min(1, Math.max(0.1, bar.position / 100)),
            }))
            .filter((bar) => bar.position > -(WAVEFORM_CONFIG.BAR_WIDTH + WAVEFORM_CONFIG.BAR_GAP));

          // Add new bar periodically
          if (now - lastUpdateTimeRef.current >= WAVEFORM_CONFIG.UPDATE_INTERVAL) {
            const barHeight = Math.max(
              WAVEFORM_CONFIG.BAR_MIN_HEIGHT,
              Math.min(
                WAVEFORM_CONFIG.BAR_MAX_HEIGHT,
                amplitude * WAVEFORM_CONFIG.AMPLITUDE_MULTIPLIER
              )
            );

            barsRef.current.push({
              height: barHeight,
              position: canvasWidth,
              opacity: 0,
            });

            lastUpdateTimeRef.current = now;
          }

          // Draw frame
          drawBars(ctx, canvasWidth, canvasHeight);

          animationFrameRef.current = requestAnimationFrame(animate);
        };

        animationFrameRef.current = requestAnimationFrame(animate);
      } catch (err) {
        console.warn('WaveformVisualizer: Failed to initialize audio', err);
      }
    };

    setup();

    return () => {
      isCancelled = true;
      window.removeEventListener('resize', handleResize);

      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      if (analyserRef.current) {
        try {
          analyserRef.current.disconnect();
        } catch (err) {
          console.warn('WaveformVisualizer: analyser disconnect failed', err);
        }
        analyserRef.current = null;
      }

      if (audioContextRef.current) {
        // Only close if the context is not already closed
        if (audioContextRef.current.state !== 'closed') {
          // Close the AudioContext to free up resources
          audioContextRef.current.close().catch((err) => {
            console.warn('WaveformVisualizer: audioContext close failed', err);
          });
        }
        audioContextRef.current = null;
      }
    };
  }, [stream, drawBars, calculateAmplitude, resizeCanvas]);

  return (
    <div
      ref={containerRef}
      className={`relative h-16 w-full overflow-hidden rounded-[10px] ${className || ''}`}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        role="img"
        aria-label={
          remainingSeconds !== undefined
            ? `Voice recording waveform visualization, ${remainingSeconds} seconds remaining`
            : 'Voice recording waveform visualization'
        }
      />
    </div>
  );
};

export default WaveformVisualizer;
